// Per-chapter copy shared by the desktop HUD (AudioHUD) and the mobile
// SVG explainer (AudioMobile) — one source of truth for title/desc/apps text.

// Scenes where speaking into the mic changes what you see.
export const MIC_SCENES = [1, 3]

export const SCENES = [
  {
    title: 'Birth of Sound',
    subtitle: 'Vibration travelling through a medium',
    desc: 'Sound is a pressure wave — particles in a medium nudge their neighbours, and the disturbance ripples outward. Nothing physical travels the distance, only the vibration does.',
    detail: 'The wave is longitudinal: particles oscillate back and forth along its direction of travel, not up and down. Its speed is set by the medium — ~343 m/s in air, roughly 4× faster in water.',
    apps: ['Microphones', 'Acoustics'],
  },
  {
    title: 'Waveforms',
    subtitle: 'Amplitude, frequency, wavelength',
    desc: 'That pressure becomes a signal over time. Amplitude sets loudness, frequency sets pitch. A deep voice and a bright voice are the same idea at different scales.',
    detail: 'To store it, we sample the curve into numbers — 16,000/sec is plenty for speech, 44,100 for music. The Nyquist limit means you can only capture frequencies up to half the sample rate.',
    apps: ['Audio capture', 'DSP'],
  },
  {
    title: 'Fourier Transform',
    subtitle: 'Every sound is a sum of frequencies',
    desc: 'Any waveform can be split into pure tones. The Fourier transform acts like a prism, decomposing speech into the bass, mid and treble bands it is built from.',
    detail: 'In practice it runs as an FFT over short overlapping windows (the STFT). There is a tradeoff: short windows pin down timing but blur pitch; long windows do the reverse.',
    apps: ['Codecs', 'EQ / filtering'],
  },
  {
    title: 'Mel-Spectrogram',
    subtitle: 'Sound visualized in time × frequency × energy',
    desc: 'Stack those frequencies over time and sound becomes a picture. Models warp it to the mel scale — spacing bands the way human hearing does — and read that image as their input.',
    detail: 'Real spectrograms are plotted in decibels, not linear, so faint harmonics survive a huge dynamic range. Formants glide as vowels change, and fricatives like "s" show up as bandless noise.',
    apps: ['mel scale', 'ElevenLabs', 'OpenAI Whisper'],
  },
  {
    title: 'VAD & Noise Removal',
    subtitle: 'Find the speech, drop the rest',
    desc: 'A Voice Activity Detector marks where speech actually is, while a denoiser strips traffic, fans and crowd noise. Classic GMM/WebRTC VAD is cheap; neural VAD is sharper — the first gate of every voice pipeline.',
    detail: 'Cheap VAD reads short-term energy and zero-crossing rate; neural VAD learns the texture of speech. Running it first lets you skip the expensive models on silence — saving most of the compute.',
    apps: ['WebRTC VAD', 'Silero', 'Krisp', 'Sarvam AI'],
  },
  {
    title: 'Speech Recognition',
    subtitle: 'Sound becomes phonemes becomes words',
    desc: 'The clean spectrogram is split into overlapping chunks (whisper-jax style) and mapped to phonemes, then words. NLP post-correction patches the word error rate when the voice breaks up.',
    detail: 'Whisper attends over ~30-second windows. CTC and attention decoders align audio to text without needing the exact timing of every phoneme hand-labelled.',
    apps: ['Whisper', 'Deepgram', 'Parakeet', 'Sarvam AI'],
  },
  {
    title: 'Audio Embeddings',
    subtitle: 'AI learns meaning, not raw sound',
    desc: 'Man, woman or child — different waveforms for the same word collapse into nearby points in a learned space. The model captures meaning, not the raw signal.',
    detail: 'These vectors live in hundreds of dimensions; cosine distance between them measures how alike two voices or sounds are — the basis of speaker verification, diarization and search.',
    apps: ['Speaker ID', 'Search', 'Diarization'],
  },
  {
    title: 'Voice Cloning',
    subtitle: 'Separating what is said from who says it',
    desc: 'Latent-space decomposition splits a voice into two streams — speech content and speaker/style. Sarvam\'s Bulbul works this way: swap the speaker vector and the same words come out in a new voice.',
    detail: 'A few seconds of reference audio can pin the speaker vector. The same trick powers cross-lingual cloning: keep the voice, swap the language it speaks.',
    apps: ['Sarvam Bulbul', 'ElevenLabs', 'Cartesia'],
  },
  {
    title: 'Text To Speech',
    subtitle: 'Text tokens become living waveforms',
    desc: 'Recognition in reverse: modern TTS predicts discrete neural-codec tokens (EnCodec / Mimi), then a diffusion or flow-matching vocoder turns those tokens into a waveform — far richer than the old spectrogram-and-Griffin-Lim path.',
    detail: 'Because codec tokens are discrete, a language model can literally "predict the next sound" the way it predicts the next word — autoregressive audio generation.',
    apps: ['EnCodec', 'ElevenLabs v3', 'Cartesia Sonic'],
  },
  {
    title: 'Emotion & Prosody',
    subtitle: 'Feeling lives in rhythm and pitch',
    desc: 'The same words can sound happy, sad or angry. Emotion lives in prosody — the rise and fall of pitch, rhythm and intensity, not the words themselves.',
    detail: 'Prosody is the F0 (pitch) contour, duration and energy over time. Two utterances with identical words but different pitch curves can carry opposite meaning — sarcasm lives here.',
    apps: ['Expressive TTS', 'Dubbing'],
  },
  {
    title: 'Realtime Voice Agents',
    subtitle: 'The full AI pipeline at millisecond speed',
    desc: 'The classic loop is Speech → STT → LLM → TTS → voice. Speech-native models (GPT-4o, Gemini Live, Moshi) collapse it — audio in, audio out, no text bottleneck. The whole p95 latency budget has to fit under ~800ms to feel alive.',
    detail: 'Latency hides everywhere: capture, endpointing, network, inference, vocoding. Streaming each stage — starting to speak before the LLM has finished thinking — is what keeps it under ~800ms.',
    apps: ['OpenAI Realtime', 'Gemini Live', 'Moshi', 'LiveKit', 'Pipecat'],
  },
  {
    title: 'Audio AI Universe',
    subtitle: 'The landscape of voice technology',
    desc: 'Recognition, cloning, dubbing, translation, enhancement, music — one core idea branching into a whole constellation of products.',
    detail: 'Every product here is the same few primitives recombined: encode audio, model it, decode audio. Master the primitives and the whole map gets small.',
    apps: ['ElevenLabs', 'Sarvam AI'],
  },
  {
    title: 'Learning Roadmap',
    subtitle: 'Your path from zero to voice AI',
    desc: 'Fundamentals → DSP → Fourier → deep learning → transformers → realtime agents. A clear route from first principles to state-of-the-art systems.',
    detail: 'Build the boring parts yourself once — a VAD, an STFT, a tiny vocoder — and the state-of-the-art papers stop reading like magic.',
    apps: ['Start building'],
  },
]
